<template>
	<form class="property-form" enctype="multipart/form-data" @submit.prevent="submitForm">
		<input type="hidden" name="_method" value="PUT">
		<div class="icon">
			<i class="fa-thin fa-file-contract"></i>
		</div>
		<errors-list :errors="errors"/>
		<div class="form-row">
			<default-field-box type="text" name="property[name]" :label="$t('forms.property_title')" :value="data.name" :disabled="!isEditing" :required="true"/>
		</div>
		<div class="form-row">
			<field-box name="property[ownership]" :label="$t('forms.ownership')" type="select" :value="data.ownership" :disabled="!isEditing" :options="ownershipOptions"/>
		</div>
		<div class="form-row">
			<field-box name="property[value]" :label="$t('forms.property_value')" :value="data.value" :placeholder="$t('forms.property_value_desc')" :disabled="!isEditing" type="number"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="property[purchase_date]" :label="$t('forms.purchase_date')" :value="data.purchase_date" :disabled="!isEditing" type="date"/>
			<field-box name="property[market_price]" :label="$t('forms.market_price')" :value="data.market_price" :disabled="!isEditing" type="number"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="property[condition]" :label="$t('forms.condition')" :value="data.condition" type="select" :disabled="!isEditing" :options="conditionOptions"/>
			<field-box name="property[purchase_price]" :label="$t('forms.purchase_price')" :value="data.purchase_price" :disabled="!isEditing" type="number"/>
		</div>
		<div class="form-row">
			<field-box name="property[financing]" :label="$t('forms.financing')" :value="data.financing" type="select" :disabled="!isEditing" :options="financingOptions"/>
		</div>
		<div class="form-row" v-if="showDetailInfoTitle">
			<h4 class="form-section-title">{{ $t('forms.detail_info')}}</h4>
		</div>
		<property-car-fieldset v-if="type === 'car'" :data="data.propertyable" :disabled="!isEditing"/>
		<property-real-estate-fieldset v-else-if="type === 'real_estate'" :data="data.propertyable" :disabled="!isEditing"/>
		<property-land-fieldset v-else-if="type === 'land'" :data="data.propertyable" :disabled="!isEditing"/>
		<property-valuable-fieldset v-else-if="type === 'valuable'" :data="data.propertyable" :disabled="!isEditing"/>
		<property-intangible-asset-fieldset v-else-if="type === 'intangible_asset'" :data="data.propertyable" :disabled="!isEditing"/>
		<property-investment-fieldset v-else-if="type === 'investment'" :disabled="!isEditing"/>
		<div class="form-row" v-if="isEditing || debts.length">
			<h4 class="form-section-title">	{{ $t('routes.debts.title') }}</h4>
		</div>
		<input type="hidden" name="property[remove_debt][]" :value="item" v-for="item in removedDebts" :key="item">
		<template v-if="isEditing">
			<template v-for="(item, index) in debts" >
				<div class="form-row" :key="item.key">
					<field-box :name="`property[debt][${index}]`" :label="$t('forms.debt')" type="select"  :value="item.value" :options="debtOptions"/>
				</div>
				<div class="form-row">
					<button type="button" class="button style-6 small" @click.stop.prevent="removeDebt(item.key)">
						<span class="inner">{{ $t('forms.remove_debt') }}</span>
					</button>
				</div>
			</template>
		</template>
		<template v-else>
			<ul class="debts-list" v-if="debts.length">
				<li v-for="item in debts" :key="item.id">
					<a href="#" @click.prevent="openDebt(item.value)">{{ getDebtName(item.value) }}</a>
				</li>
			</ul>
		</template>
		<div class="form-row" v-if="isEditing">
			<button type="button" class="button style-2" @click.prevent="addDebt">
				<span class="inner">{{ $t('forms.add_debt') }}</span>
			</button>
		</div>
		<div class="form-actions">
			<button class="button style-4" type="button" v-if="!isEditing" @click.prevent="toggleEdit">
				<span class="inner">{{ $t('forms.edit') }}</span>
			</button>
			<button class="button style-4" type="submit" :class="loading ? 'loading' : ''" v-else>
				<span class="inner">{{ $t('forms.save') }}</span>
			</button>
			<button class="button style-1" type="button"
			        @click="$store.commit('HIDE_PROPERTY_EDIT_FORM')">
				<span class="inner">{{ $t('forms.cancel') }}</span>
			</button>
		</div>
	</form>
</template>

<script>
import DefaultFieldBox from "@/components/form-fields/DefaultFieldBox.vue";
import ErrorsList from "@/components/containers/ErrorsList.vue";
import FieldBox from "@/components/form-fields/FieldBox.vue";
import { trackEvent } from '@/analytics';
import PropertyCarFieldset from "@/components/fieldsets/PropertyCarFieldset.vue";
import PropertyRealEstateFieldset from "@/components/fieldsets/PropertyRealEstateFieldset.vue";
import PropertyLandFieldset from "@/components/fieldsets/PropertyLandFieldset.vue";
import PropertyValuableFieldset from "@/components/fieldsets/PropertyValuableFieldset.vue";
import PropertyIntangibleAssetFieldset from "@/components/fieldsets/PropertyIntangibleAssetFieldset.vue";
import PropertyInvestmentFieldset from "@/components/fieldsets/PropertyInvestmentFieldset.vue";

export default {
	name: "PropertyEditForm",
	props: ['data'],
	components: {
		FieldBox,
		ErrorsList,
		DefaultFieldBox,
		PropertyCarFieldset,
		PropertyRealEstateFieldset,
		PropertyLandFieldset,
		PropertyValuableFieldset,
		PropertyIntangibleAssetFieldset,
		PropertyInvestmentFieldset,
	},
	data() {
		return {
			loading: false,
			errors: false,
			type: '',
			debtsCount: 0,
			debts: [],
			debtKey: 0,
			removedDebts: [],
			isEditing: false,
		}
	},
	computed:{
		debtOptions(){
			let debts = this.$store.state.views.debts;
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				}
			];

			debts.forEach(item => {
				options.push({
					value: `${item.type}_${item.id}`,
					title: item.name
				});
			});

			return options;
		},
		showDetailInfoTitle(){
			let options = [
				'car',
				'real_estate',
				'land',
				'valuable',
				'intangible_asset',
				'investment'
			];

			return options.includes(this.type);
		},
		typeOptions(){
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'car',
					title: this.$t('forms.property_car'),
				},
				{
					value: 'real_estate',
					title: this.$t('forms.property_real_estate'),
				},
				{
					value: 'land',
					title: this.$t('forms.property_land'),
				},
				{
					value: 'valuable',
					title: this.$t('forms.property_valuable'),
				},
				{
					value: 'intangible_asset',
					title: this.$t('forms.property_intangible_asset'),
				},
				{
					value: 'investment',
					title: this.$t('forms.property_investment'),
				},
				{
					value: 'other',
					title: this.$t('forms.property_other'),
				}
			];

			options.forEach(item => {
				if (item.value === this.data.type) {
					item.selected = true;
				}
			});

			return options;
		},
		ownershipOptions() {
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'personal',
					title: this.$t('forms.ownership_personal'),
				},
				{
					value: 'join',
					title: this.$t('forms.ownership_join'),
				}
			];

			options.forEach(item => {
				if (item.value === this.data.ownership) {
					item.selected = true;
				}
			});

			return options;
		},
		conditionOptions(){
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'low',
					title: this.$t('forms.condition_low')
				},
				{
					value: 'average',
					title: this.$t('forms.condition_average')
				},
				{
					value: 'good',
					title: this.$t('forms.condition_good')
				},
				{
					value: 'excellent',
					title: this.$t('forms.condition_excellent')
				},
			];

			options.forEach(item => {
				if (item.value === this.data.condition) {
					item.selected = true;
				}
			});

			return options;
		},
		financingOptions(){
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'paid',
					title: this.$t('forms.financing_paid')
				},
				{
					value: 'loan',
					title: this.$t('forms.financing_loan')
				},
			];

			options.forEach(item => {
				if (item.value === this.data.financing) {
					item.selected = true;
				}
			});

			return options;
		}
	},
	methods: {
		getDebtName(value){
			let debts = this.$store.state.views.debts;
			let debtId = parseInt(value.split('_')[1]);

			let debt = debts.find(item => item.id === debtId);
			if(!debt){
				return '';
			}

			return debt.name;
		},
		openDebt(value){
			let [type, id] = value.split('_');

			if(type === 'document'){
				this.showDocumentDetail(id);
			} else {
				this.showContractDetail(id);
			}
		},
		showDocumentDetail(id) {
			this.$store.commit('SET_APP_LOADING');

			let promises = [];
			if (!this.$store.state.views.document_categories.length) {
				let documentCategoriesPromise = this.axios.get(`/api/document-categories`);
				promises.push(documentCategoriesPromise);
			}

			let documentPromise = this.axios.get(`/api/documents/${id}`);
			promises.push(documentPromise);

			Promise.all(promises).then((values) => {
				values.forEach(response => {
					if (response.data.document_categories) {
						this.$store.dispatch('setDocumentCategories', response.data.document_categories);
					}
					if (response.data.document) {
						this.$store.commit('HIDE_PROPERTY_EDIT_FORM');
						setTimeout(() => {
							this.$store.commit('SHOW_DOCUMENTS_EDIT_FORM', response.data.document);
						}, 400);

						this.$store.commit('UNSET_APP_LOADING');
						return false;
					}
					this.$store.commit('UNSET_APP_LOADING');
				});
			}).catch(() => {
				this.$store.commit('UNSET_APP_LOADING');
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: this.$t('general.default_error')
				});
			});
		},
		showContractDetail(id) {
			this.$store.commit('SET_APP_LOADING');
			this.axios.get(`/api/contracts/${id}`).then(response => {
				this.$store.commit('UNSET_APP_LOADING');
				this.$store.commit('HIDE_PROPERTY_EDIT_FORM');
				setTimeout(() => {
					this.$store.commit('SHOW_CONTRACTS_EDIT_FORM', response.data.contract);
				}, 400);

				return false;
			}).catch(error => {
				this.$store.commit('UNSET_APP_LOADING');
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: error.response.data.message
				});
			});
		},
		toggleEdit(){
			this.isEditing = !this.isEditing;
		},
		addDebt(){
			this.debts.push({
				key: this.debtKey,
			});
			this.debtKey++;
		},
		removeDebt(key){
			let debt = this.debts.find(item => {
				return item.key === key;
			});

			if(debt){
				this.removedDebts.push(debt.id);
			}

			this.debts = this.debts.filter(item => {
				return key !== item.key;
			});
		},
		handleFieldChanged(data){
			switch (data.name){
				case 'property[type]':
					this.type = data.value;
					break;
			}
		},
		submitForm(e) {
			let form = e.target;
			let formData = new FormData(form);

			this.loading = true;
			trackEvent('form_submit', { form_name: 'property_edit', module: 'properties' });
			this.axios.post(`/api/properties/${this.data.id}`, formData).then(response => {
				this._submitSuccess = true;
				trackEvent('form_submit_success', { form_name: 'property_edit', module: 'properties' });
				form.reset();
				this.loading = false;
				this.$store.dispatch('updateProperty', response.data.property);
				this.$store.commit('HIDE_PROPERTY_EDIT_FORM');
			}).catch(error => {
				trackEvent('form_submit_error', {
					form_name: 'property_edit',
					module: 'properties',
					error_message: error.response && error.response.data ? error.response.data.message : error.message,
					error_status: error.response ? String(error.response.status) : null,
				});
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: error.response.data.message
				});

				if (error.response.data.errors) {
					this.errors = error.response.data.errors;
				}

				this.loading = false;
			});
		},
	},
	mounted(){
		this._submitSuccess = false;
		trackEvent('form_open', { form_name: 'property_edit', module: 'properties' });
		this.$root.$on('field-changed', this.handleFieldChanged);
		this.type = this.data.type;
		if(this.data.debts){
			this.data.debts.forEach(item => {
				let type = item.debtable_type.toLowerCase();
				if(type.includes('contract')){
					type = 'contract';
				} else {
					type = 'document';
				}

				this.debts.push({
					id: item.id,
					key: this.debtKey,
					value: `${type}_${item.debtable_id}`
				});
				this.debtKey++;
			});
		}
	},
	beforeDestroy() {
		if (!this._submitSuccess) {
			trackEvent('form_close', { form_name: 'property_edit', module: 'properties' });
		}
	},
	destroyed() {
		this.$root.$off('field-changed');
	}
}
</script>

<style scoped lang="scss">
.light-mode {
	.debts-list {
		a {
			color: var(--font-dark-color);
		}
	}
}
.debts-list {
	list-style-type: disc;
	padding-left: 16px;
	font-size: 14px;
	a {
		color: var(--white-color);
		&:hover {
			color: var(--primary-color);
		}
	}
	li + li {
		margin-top: 5px;
	}
}
fieldset {
	margin-bottom: 15px;
}
.property-form {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
}

.form-group {
	label {
		font-size: 12px;
		text-align: center;
		text-transform: uppercase;
		font-weight: 600;
	}
}
.button {
	width: 100%;
}


.icon {
	text-align: center;
	font-size: 21px;
	margin-bottom: 10px;

	&:last-child {
		margin-bottom: 0;
	}
}

.form-row {
	margin-bottom: 15px;

	&:last-child {
		margin-bottom: 0;
	}

	&.two-columns {
		display: flex;
		justify-content: space-between;
		flex-direction: row;

		.field-box {
			width: calc(50% - 8px);
		}
	}
}

.form-actions {

	display: flex;
	flex-direction: column;
	margin-top: 15px;

	.button {
		margin-bottom: 10px;

		&:last-child {
			margin-bottom: 0;
		}
	}
}

@media($mobileS) {
	.form-actions {
		margin-top: 25px;
	}
	.form-row {
		margin-bottom: 10px;

		&.two-columns {
			flex-direction: column;

			.field-box {
				width: 100%;
				margin-bottom: 10px;

				&:last-child {
					margin-bottom: 0;
				}
			}
		}
	}
}
</style>